import React, {
    useState, useEffect, useCallback, useRef
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import ProductList from 'components/Content/ProductList';
import ProductTable from 'components/ProductPage/ProductTable';
import BccCarousel from 'components/Bcc/BccCarousel/BccCarousel';
import { wrapFunctionalComponent } from 'utils/framework';
import BCCUtils from 'utils/BCC';
import PropTypes from 'prop-types';
import ProductItem from 'components/Product/ProductItem';
import { CONSTRUCTOR_PODS } from 'constants/constructorConstants';
import { Grid, Text } from 'components/ui';
import { fontSizes, fonts, space } from 'style/config';
import { recommendationsServiceSelector } from 'selectors/recommendationsService/recommendationsServiceSelector';
import preferredStoreInfoSelector from 'selectors/user/preferredStoreInfoSelector';
import { showChosenForYouMlTagsSelector } from 'viewModel/selectors/testTarget/showChosenForYouMlTagsSelector';
import recServiceUtils from 'utils/RecommendationService';
import getProductRecommendations from 'services/api/productRecommendations/getProductRecommendations';
import { SET_RECOMMENDATIONS, SET_RECOMMENDATIONS_FAILURE } from 'constants/actionTypes/recommendationsService';
import GraphQLException from 'exceptions/GraphQLException';
import Empty from 'constants/empty';

const { COMPONENT_NAMES, IMAGE_SIZES } = BCCUtils;
const { transformRecommendationsForCarousel, getSeedItems } = recServiceUtils;

const RecServiceCarousel = props => {
    const {
        params = {},
        podId,
        title = '',
        skuList = [],
        onConstructorFailed = () => {},
        currentProduct,
        showTitle,
        isSmallView,
        isCAMobile = false,
        closeParentModal,
        customStyles,
        showBasketGreyBackground,
        customTitle,
        scrollPadding,
        grouping,
        useSimplifiedCarouselTracking = false,
        page,
        ...restProps
    } = props;

    // Redux selectors
    const dispatch = useDispatch();
    const { recommendations = {} } = useSelector(recommendationsServiceSelector, shallowEqual);
    const preferredStoreInfo = useSelector(preferredStoreInfoSelector, shallowEqual);
    const preferredStoreId = preferredStoreInfo?.storeId || null;
    const isChosenForYouMlTagsEnabled = useSelector(showChosenForYouMlTagsSelector);
    const showMlTags = isChosenForYouMlTagsEnabled && podId === CONSTRUCTOR_PODS.RFY && page === 'home';
    const mlTagsProfileId = useSelector(state => (showMlTags ? state.user?.profileId || null : null));
    // Local state
    const [localRecommendations, setLocalRecommendations] = useState([]);
    const [displayTitle, setDisplayTitle] = useState(title || '');
    const [showSkeleton, setShowSkeleton] = useState(true);
    const [resultId, setResultId] = useState(null);
    const [totalResults, setTotalResults] = useState(null);

    // Helper to build convenience hub params
    const getConvenienceHubParams = useCallback(storeId => {
        return storeId
            ? {
                filters: [{ key: 'availability', value: `store_${storeId}` }]
            }
            : {};
    }, []);

    // Process recommendations from Redux store
    const processRecommendations = recsData => {
        const currentRecs = recsData[podId];

        if (currentRecs?.isError || !currentRecs) {
            setLocalRecommendations(skuList || []);
            setDisplayTitle(title || '');
            onConstructorFailed();
        } else {
            // Transform raw recommendations from GraphQL structure to carousel-expected structure
            const rawRecommendations = currentRecs.recommendations || [];
            const transformedRecommendations = transformRecommendationsForCarousel(rawRecommendations, podId);

            setLocalRecommendations(transformedRecommendations);
            setDisplayTitle(currentRecs.displayName || title || '');
            setResultId(currentRecs.resultId || null);
            setTotalResults(currentRecs.totalNumResults || null);
        }

        if (currentRecs !== undefined) {
            setShowSkeleton(false);
        }
    };

    // Conditional dependency: only track preferredStoreId for convenience hub
    const isConvenienceHub = podId === CONSTRUCTOR_PODS.RFY_CONVENIENCE_HUB;
    const fetchDependency = isConvenienceHub ? preferredStoreId : null;

    const fetchStateRef = useRef({ latestId: 0, latestHadMlTags: false });

    const fetchAndDispatch = useCallback(
        async payload => {
            const myId = ++fetchStateRef.current.latestId;
            fetchStateRef.current.latestHadMlTags = !!payload.showMlTags;

            const isSuperseded = () => fetchStateRef.current.latestHadMlTags && fetchStateRef.current.latestId !== myId;

            try {
                setShowSkeleton(true);
                const { recommendations: recs } = await getProductRecommendations(payload);

                if (isSuperseded()) {
                    return;
                }

                dispatch({ type: SET_RECOMMENDATIONS, payload: recs || Empty.Object });
            } catch (error) {
                setShowSkeleton(false);

                if (isSuperseded()) {
                    return;
                }

                dispatch({
                    type: SET_RECOMMENDATIONS_FAILURE,
                    payload: {
                        podId: payload.podId,
                        error:
                            error instanceof GraphQLException
                                ? error
                                : {
                                    message: error.message || 'An error occurred while fetching product recommendations',
                                    status: 500
                                }
                    }
                });
            }
        },
        [dispatch]
    );

    // Mount effect - initial data fetch
    useEffect(() => {
        if (isConvenienceHub && !preferredStoreId) {
            return;
        }

        const requestPayload = isConvenienceHub
            ? {
                podId,
                ...getConvenienceHubParams(preferredStoreId),
                ...(showMlTags && { showMlTags: true })
            }
            : {
                podId,
                ...params,
                ...(showMlTags && { showMlTags: true })
            };

        fetchAndDispatch(requestPayload);

        // Process if data already exists (SSR scenario)
        if (recommendations[podId]) {
            processRecommendations(recommendations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchDependency, showMlTags, mlTagsProfileId]);

    // Process recommendations when Redux state updates
    useEffect(() => {
        if (recommendations[podId]) {
            processRecommendations(recommendations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendations, podId]);

    // Constructor tracking: seed item(s) sent in the recommendations API call for this pod
    const seedItems = getSeedItems(podId, params);

    // Render carousel based on podId
    const renderCarousel = () => {
        switch (podId) {
            case CONSTRUCTOR_PODS.SIMILAR_PRODUCTS:
                return isCAMobile ? (
                    <ProductList
                        {...restProps}
                        skuList={localRecommendations}
                        title={displayTitle}
                        grouping={grouping}
                        showSkeleton={showSkeleton}
                        podId={podId}
                        customStyles={customStyles}
                        resultId={resultId}
                        totalResults={totalResults}
                        isCarousel={true}
                    />
                ) : (
                    <ProductTable
                        skuList={localRecommendations}
                        isSmallView={isSmallView}
                        currentProduct={currentProduct}
                        showTitle={showTitle}
                        title={displayTitle}
                        podId={podId}
                        resultId={resultId}
                        totalResults={totalResults}
                        isCarousel={true}
                    />
                );
            case CONSTRUCTOR_PODS.ATB:
            case CONSTRUCTOR_PODS.ORDER_CONFIRMATION:
            case CONSTRUCTOR_PODS.COMMUNITY_PROFILE:
                return (
                    <BccCarousel
                        {...restProps}
                        showArrows={true}
                        showTouts={Sephora.isMobile()}
                        title={displayTitle}
                        mobileWebTitleText={displayTitle}
                        carouselItems={localRecommendations}
                        totalItems={localRecommendations.length}
                        componentType={COMPONENT_NAMES.CAROUSEL}
                        podId={podId}
                        resultId={resultId}
                        totalResults={totalResults}
                        seedItems={seedItems}
                        isCarousel={true}
                    />
                );
            case CONSTRUCTOR_PODS.PURCHASE_HISTORY:
            case CONSTRUCTOR_PODS.SIMILAR_PRODUCTS_CONTENT_PAGE:
            case CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT:
                return (
                    <React.Fragment>
                        <Text
                            is='h3'
                            lineHeight='none'
                            css={
                                podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT
                                    ? { fontSize: fontSizes.lg, textAlign: 'left', fontWeight: 'var(--font-weight-bold)', marginBottom: space[4] }
                                    : {
                                        fontSize: fontSizes.xl,
                                        textAlign: 'center',
                                        marginTop: space[3],
                                        fontFamily: fonts.serif,
                                        marginBottom: space[5]
                                    }
                            }
                            children={displayTitle}
                        />
                        <Grid
                            gap={5}
                            columns={[2, 5]}
                            data-cnstrc-recommendations
                            data-cnstrc-recommendations-pod-id={podId}
                            data-cnstrc-result-id={resultId}
                            data-cnstrc-num-results={totalResults}
                            data-cnstrc-recommendations-seed-items={seedItems}
                        >
                            {localRecommendations.map((item, index) => (
                                <ProductItem
                                    podId={podId}
                                    formatValuePrice={true}
                                    position={index}
                                    key={item.skuId}
                                    imageSize={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT ? 132 : IMAGE_SIZES[135]}
                                    showQuickLook={true}
                                    showPrice={true}
                                    showReviews={true}
                                    showBadges={true}
                                    showMarketingFlags={true}
                                    useAddToBasket={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT}
                                    isAddButton={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT}
                                    closeParentModal={closeParentModal}
                                    isCleanAtSephora={podId === CONSTRUCTOR_PODS.CLEAN_HIGHLIGHT}
                                    {...item}
                                />
                            ))}
                        </Grid>
                    </React.Fragment>
                );
            default:
                return (
                    <ProductList
                        {...restProps}
                        skuList={localRecommendations}
                        title={customTitle || displayTitle}
                        grouping={grouping}
                        showSkeleton={showSkeleton}
                        podId={podId}
                        customStyles={customStyles}
                        showBasketGreyBackground={showBasketGreyBackground}
                        resultId={resultId}
                        totalResults={totalResults}
                        seedItems={seedItems}
                        scrollPadding={scrollPadding}
                        useSimplifiedCarouselTracking={useSimplifiedCarouselTracking}
                        showMlTags={showMlTags}
                    />
                );
        }
    };

    return showSkeleton || localRecommendations?.length > 0 ? renderCarousel() : null;
};

RecServiceCarousel.propTypes = {
    podId: PropTypes.string.isRequired,
    params: PropTypes.object,
    isCollection: PropTypes.bool,
    title: PropTypes.string,
    skuList: PropTypes.array,
    onConstructorFailed: PropTypes.func,
    currentProduct: PropTypes.object,
    showTitle: PropTypes.bool,
    isSmallView: PropTypes.bool,
    isCAMobile: PropTypes.bool,
    closeParentModal: PropTypes.func,
    customStyles: PropTypes.object,
    showBasketGreyBackground: PropTypes.bool,
    customTitle: PropTypes.string,
    scrollPadding: PropTypes.string,
    grouping: PropTypes.array,
    useSimplifiedCarouselTracking: PropTypes.bool
};

export default wrapFunctionalComponent(RecServiceCarousel, 'RecServiceCarousel');
